import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { EmployeesProvider } from './features/settings/employees/context';
import { DepartmentsProvider } from './features/settings/departments/context';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import Home from './pages/Home';
import NewLogin from './pages/auth/NewLogin';
import ForgotPassword from './pages/auth/ForgotPassword';
// Import patient management components
import { PatientListPage, PatientDetailPage, PatientForm } from './features/patients';
import { 
  FinancialReport, 
  ServiceReport, 
  AppointmentReport, 
  PatientStatementReport 
} from './pages/reports';
import PendingPaymentReport from './pages/reports/PendingPaymentReport';
import PendingDispensingReport from './pages/reports/PendingDispensingReport';
import CollectionReport from './pages/reports/CollectionReport';
import ItemLedgerReport from './features/inventory/pages/ItemLedgerReport';
import StockBalanceReport from './features/inventory/pages/StockBalanceReport';
import FSNAnalysisReport from './features/inventory/pages/FSNAnalysisReport';
import { CompanyProfile } from './pages/settings';
// Location management is now handled by the locations feature module
import { FacilitiesPage } from './pages/facilities';
import FacilityForm from './pages/facilities/FacilityForm';
import FacilityView from './pages/facilities/FacilityView';
import { DepartmentRoutes } from './features/settings/departments';
import { LocationRoutes } from './features/settings/locations';
import { UserRoleList } from './pages/userRoles';
import UsersList from './pages/users/UsersList';
import BookAppointment from './pages/public/BookAppointment';
import { EmployeesPage, EmployeeDetailPage } from './features/settings/employees/pages';
import DoctorAvailabilityPage from './pages/employees/DoctorAvailabilityPage';
import { ItemGroupList, ItemGroupForm, ItemGroupView } from './pages/settings/itemGroups';
import { UnitOfMeasureList, UnitOfMeasureForm, UnitOfMeasureView } from './pages/settings/unitOfMeasures';
import ItemsPage from './features/settings/items/pages/ItemsPage';
import ProfilePage from './pages/profile/ProfilePage';
import MenuList from './pages/menu/MenuList';
import MenuForm from './pages/menu/MenuForm';
// Import AppointmentsPage from features
import { AppointmentsPage } from './features/appointments';
import { AppointmentsProvider } from './features/appointments/context/AppointmentsContext';
import { ChartOfAccountsPage } from './features/chart-of-accounts';
import { CreditDebitTrackerPage, TransactionReport } from './features/credit-debit-tracker';
import SchemaReviewPage from './features/appointments/pages/SchemaReviewPage';
// Import Inventory Pages
import InventoryPage from './pages/inventory/InventoryPage';
import StockInPage from './pages/inventory/StockInPage';
import StockOutPage from './pages/inventory/StockOutPage';
import StockTransferPage from './pages/inventory/StockTransferPage';
import InventoryTransactionsPage from './features/inventory/pages/InventoryPage';
import Dashboard from './pages/dashboard/Dashboard';
import MainLayout from './components/layout/MainLayout';

// Import the new ErrorBoundary
import ErrorBoundary from './components/ErrorBoundary';

// Protected route component - allows all authenticated users
const ProtectedRoute: React.FC<{ 
  children?: React.ReactNode;
  allowedRoles?: any; // Not used in current implementation but kept for future use
}> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [authChecked, setAuthChecked] = React.useState(false);
  const [isInitialCheck, setIsInitialCheck] = React.useState(true);
  
  // Only show loading state for a short time to prevent infinite loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setAuthChecked(true);
      // After initial check, mark that we've done the initial auth check
      setIsInitialCheck(false);
    }, 1000); // 1 second max wait for auth check
    
    return () => clearTimeout(timer);
  }, []);
  
  // Show loading state while checking auth (but only for a short time)
  if ((loading || !authChecked) && location.pathname !== '/login') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: 'background.default',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="textSecondary">
          Loading application...
        </Typography>
      </Box>
    );
  }

  // If we're on the login page but already authenticated, redirect to dashboard
  if (isAuthenticated && location.pathname === '/login') {
    const from = (location.state as any)?.from?.pathname || '/dashboard';
    // Only redirect if we're not already on the target page to prevent loops
    if (location.pathname !== from) {
      console.log('[AUTH] Already authenticated, redirecting to:', from);
      return <Navigate to={from} replace />;
    }
  }
  
  // If not authenticated and not on login page, redirect to login
  if (!isAuthenticated && location.pathname !== '/login' && !isInitialCheck) {
    console.log('[AUTH] Not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If on login page and not authenticated, render children (login form)
  if (location.pathname === '/login' && !isAuthenticated) {
    return <>{children}</>;
  }
  
  // If authenticated and not on login page, render children
  return <>{children}</>;
};

// Public route component - only for non-authenticated users
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [initialized, setInitialized] = React.useState(false);
  
  // Wait for auth to initialize before making any decisions
  React.useEffect(() => {
    if (!loading) {
      setInitialized(true);
    }
  }, [loading]);
  
  // Show loading state while checking auth
  if (!initialized || loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If authenticated and trying to access public route, redirect to dashboard
  if (isAuthenticated) {
    // Only redirect if we're on a public route that should be hidden when authenticated
    const isPublicRoute = ['/login', '/register', '/forgot-password'].includes(location.pathname);
    if (isPublicRoute) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      return <Navigate to={from} replace />;
    }
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      
      <Route path="/book-appointment" element={
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <BookAppointment />
        </LocalizationProvider>
      } />
      
      <Route path="/login" element={
        <PublicRoute>
          <NewLogin />
        </PublicRoute>
      } />
      
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      
      {/* Protected Routes - Wrap all protected routes with MainLayout */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        {/* Nested routes will be rendered in the MainLayout's Outlet */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="facilities">
  <Route index element={<FacilitiesPage />} />
  <Route path="view/:id" element={<FacilityView />} />
  <Route path="edit/:id" element={<FacilityForm />} />
  <Route path="new" element={<FacilityForm />} />
</Route>
        
        {/* Appointments - Top Level Route for Backward Compatibility */}
        <Route path="appointments" element={
          <AppointmentsProvider>
            <AppointmentsPage />
          </AppointmentsProvider>
        } />

        {/* Patient Management */}
        <Route path="patient-management">
          <Route index element={<Navigate to="patients" replace />} />
          
          {/* Patients */}
          <Route path="patients">
            <Route index element={<PatientListPage />} />
            <Route path="new" element={<PatientForm />} />
            <Route path=":id">
              <Route index element={<PatientDetailPage />} />
              <Route path="edit" element={<PatientForm isEdit />} />
            </Route>
          </Route>
          
          {/* Appointments */}
          <Route path="appointments">
            <Route index element={
              <AppointmentsProvider>
                <AppointmentsPage />
              </AppointmentsProvider>
            } />
            <Route path="schema-review" element={
              <AppointmentsProvider>
                <SchemaReviewPage />
              </AppointmentsProvider>
            } />
          </Route>
        </Route>
        
        {/* Settings */}
        <Route path="settings">
          <Route index element={<Navigate to="company" replace />} />
          <Route path="company" element={<CompanyProfile />} />
          
          {/* Employee Management */}
          <Route path="employees">
            <Route index element={
              <EmployeesProvider>
                <EmployeesPage />
              </EmployeesProvider>
            } />
            <Route path="new" element={<EmployeesProvider><EmployeeDetailPage /></EmployeesProvider>} />
            <Route path=":id" element={<EmployeesProvider><EmployeeDetailPage /></EmployeesProvider>} />
            <Route path=":id/edit" element={<EmployeesProvider><EmployeeDetailPage /></EmployeesProvider>} />
            <Route path=":id/availability" element={<EmployeesProvider><DoctorAvailabilityPage /></EmployeesProvider>} />
          </Route>
          
          {/* Role Management */}
          <Route path="roles">
            <Route index element={<UserRoleList />} />
          </Route>

          {/* Menu Management */}
          <Route path="menus">
            <Route index element={<MenuList />} />
            <Route path="new" element={<MenuForm />} />
            <Route path="edit/:id" element={<MenuForm />} />
            <Route path=":id" element={<MenuForm />} />
          </Route>
          
          {/* User Management */}
          <Route path="users">
            <Route index element={<UsersList />} />
          </Route>
          
          {/* Facilities Management */}
          <Route path="facilities/view/:id" element={<FacilityView />} />
          {/* Department Management */}
          <Route path="departments/*" element={<DepartmentRoutes />} />
          
          {/* Location Management */}
          <Route path="locations/*" element={<LocationRoutes />} />
          
          {/* Items Management */}
          <Route path="items/*" element={<ItemsPage />} />
          
          {/* Item Groups Management */}
          <Route path="item-groups">
            <Route index element={<ItemGroupList />} />
            <Route path="new" element={<ItemGroupForm />} />
            <Route path=":id" element={<ItemGroupView />} />
            <Route path=":id/edit" element={<ItemGroupForm />} />
          </Route>
          
          {/* Unit of Measures Management */}
          <Route path="unit-of-measures">
            <Route index element={<UnitOfMeasureList />} />
            <Route path="new" element={<UnitOfMeasureForm />} />
            <Route path=":id" element={<UnitOfMeasureView />} />
            <Route path=":id/edit" element={<UnitOfMeasureForm />} />
          </Route>
        </Route>
        
        {/* Accounts Management */}
        <Route path="accounts">
          <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
          <Route path="transactions" element={<CreditDebitTrackerPage />} />
          <Route path="transaction-report" element={<TransactionReport />} />
        </Route>
        
        {/* Inventory Management */}
        <Route path="inventory">
          <Route index element={<InventoryPage />} />
          <Route path="transactions/*" element={<InventoryTransactionsPage />} />
          <Route path="stock-in" element={<StockInPage />} />
          <Route path="stock-out" element={<StockOutPage />} />
          <Route path="stock-transfer" element={<StockTransferPage />} />
          <Route path="reports">
            <Route path="item-ledger" element={<ItemLedgerReport />} />
            <Route path="stock-balance" element={<StockBalanceReport />} />
            <Route path="fsn-analysis" element={<FSNAnalysisReport />} />
          </Route>
        </Route>
        
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />
        
        {/* Reports Section */}
        <Route path="reports">
          <Route path="financial" element={<FinancialReport />} />
          <Route path="collection" element={<CollectionReport />} />
          <Route path="services" element={<ServiceReport />} />
          <Route path="appointments" element={<AppointmentReport />} />
          <Route path="pending-payments" element={<PendingPaymentReport />} />
          <Route path="pending-dispensing" element={<PendingDispensingReport />} />
          <Route path="patient-statement" element={<PatientStatementReport />} />
          <Route path="patient-statement/:patientId" element={<PatientStatementReport />} />
          <Route path="transactions" element={<TransactionReport />} />
        </Route>
        
        {/* Doctor Section */}
       
        
        {/* 404 - Not Found */}
        <Route path="*" element={
          <Box sx={{ p: 3 }}>
            <Typography variant="h4">404 - Page Not Found</Typography>
          </Box>
        } />
      </Route>
      
      {/* Catch all other routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <CssBaseline />
      <ErrorBoundary context="App">
        <AuthProvider>
          <DepartmentsProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <AppContent />
            </LocalizationProvider>
          </DepartmentsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
